import type { LocationQuery, LocationQueryRaw, RouteLocationRaw } from 'vue-router';

/**
 * Returns route query params with stable key ordering so SSR and client produce
 * identical href strings for RouterLink / language-switch links. Bare params
 * (`null` values) are preserved so `?preview` survives language switches; only
 * `undefined` entries are dropped. Array value order is preserved to avoid
 * changing observable repeated-param order for downstream tracking.
 */
export const normalizeRouteQuery = (query: LocationQuery): LocationQueryRaw => {
  const normalized: LocationQueryRaw = {};

  Object.keys(query)
    .sort((left, right) => left.localeCompare(right))
    .forEach((key) => {
      const value = query[key];

      if (value === undefined) {
        return;
      }

      if (Array.isArray(value)) {
        normalized[key] = value.map((item) =>
          item === null || item === undefined ? null : String(item),
        );
        return;
      }

      normalized[key] = value;
    });

  return normalized;
};

export const withNormalizedRouteQuery = (location: RouteLocationRaw): RouteLocationRaw => {
  if (typeof location === 'string') {
    return location;
  }

  const { query } = location;

  if (!query || typeof query === 'string') {
    return location;
  }

  return {
    ...location,
    query: normalizeRouteQuery(query as LocationQuery),
  };
};
